// src/utils/statusSheetLayout.js
// Per-user print layout for the Status Sheet: what order the sections come in,
// which ones print, which start a fresh page, and what each is called.
//
// Pure — no Firestore, no React. The page owns loading/saving; this owns the
// rules, so the awkward cases (a saved layout from before a section existed, a
// section we later removed, a blank rename) are all testable.

// The sheet's sections, in the order they've always printed. The old single
// "Active Listings" block is now five independent sections so pending deals
// can sit between listing statuses (Henry's order, 2026-08-17). `label` is
// the default heading; a user rename overrides it. Saves from the 4-section
// era migrate on load: normalizeLayout drops the retired "listings" key and
// slots these into their default places.
export const DEFAULT_SECTIONS = [
  { key: "pending", label: "Pending Deals" },
  { key: "active", label: "Active Listings" },
  { key: "coming-soon", label: "Coming Soon" },
  { key: "premarket", label: "Pre-Market" },
  { key: "premarket-next", label: "Pre-Market Next Year" },
  { key: "on-hold", label: "On Hold" },
  { key: "appointments", label: "Listing Appointments" },
  { key: "leads", label: "Buyers & Leads" },
];

export const DEFAULT_SECTION_KEYS = DEFAULT_SECTIONS.map((s) => s.key);

const defaultFor = (key) => DEFAULT_SECTIONS.find((s) => s.key === key);

// Turn whatever was saved into a complete, ordered, trustworthy layout.
//
// Deliberately forgiving, because a saved layout outlives the code that wrote
// it: entries for sections that no longer exist are dropped, sections added to
// the sheet since the save are appended in their default position rather than
// vanishing from the printout, and a blank rename falls back to the default
// heading instead of printing an untitled section.
export function normalizeLayout(saved) {
  const rows = Array.isArray(saved) ? saved : [];
  const seen = new Set();
  const out = [];

  for (const row of rows) {
    const key = row && typeof row === "object" ? row.key : row;
    if (!defaultFor(key) || seen.has(key)) continue;
    seen.add(key);
    const label = String((row && row.label) || "").trim();
    out.push({
      key,
      label: label || defaultFor(key).label,
      hidden: !!(row && row.hidden),
      pageBreak: !!(row && row.pageBreak),
    });
  }

  // Anything the save didn't know about keeps its default place in the order.
  DEFAULT_SECTIONS.forEach((s, i) => {
    if (seen.has(s.key)) return;
    out.splice(Math.min(i, out.length), 0, {
      key: s.key,
      label: s.label,
      hidden: false,
      pageBreak: false,
    });
  });

  return out;
}

// The sections that actually print, in order.
export const visibleSections = (layout) =>
  normalizeLayout(layout).filter((s) => !s.hidden);

// Move one section up or down. Out-of-range moves are a no-op rather than an
// error — the arrows at the ends of the list simply do nothing.
export function moveSection(layout, key, delta) {
  const rows = normalizeLayout(layout);
  const from = rows.findIndex((s) => s.key === key);
  if (from < 0) return rows;
  const to = from + delta;
  if (to < 0 || to >= rows.length) return rows;
  const next = rows.slice();
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row);
  return next;
}

// Drag-and-drop: put `key` at `toIndex`, shuffling the rest along.
export function reorderSection(layout, key, toIndex) {
  const rows = normalizeLayout(layout);
  const from = rows.findIndex((s) => s.key === key);
  if (from < 0 || toIndex < 0 || toIndex >= rows.length) return rows;
  const next = rows.slice();
  const [row] = next.splice(from, 1);
  next.splice(toIndex, 0, row);
  return next;
}

// Set one field on one section (hidden, pageBreak, label).
// Sanitizes the single row in place — running it back through normalizeLayout
// would re-insert the OTHER three sections around it and hand back the wrong
// one.
export function updateSection(layout, key, patch) {
  return normalizeLayout(layout).map((s) => {
    if (s.key !== key) return s;
    const merged = { ...s, ...patch };
    const label = String(merged.label || "").trim();
    return {
      key: s.key,
      label: label || defaultFor(s.key).label,
      hidden: !!merged.hidden,
      pageBreak: !!merged.pageBreak,
    };
  });
}

// Back to the shipped order, everything visible, default headings.
export const resetLayout = () => normalizeLayout(null);

// True when the user has changed nothing — lets the UI hide a pointless Reset.
export const isDefaultLayout = (layout) => {
  const rows = normalizeLayout(layout);
  return rows.every(
    (s, i) =>
      s.key === DEFAULT_SECTIONS[i].key &&
      s.label === DEFAULT_SECTIONS[i].label &&
      !s.hidden &&
      !s.pageBreak,
  );
};
